import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Puzzle } from 'lucide-react';
import { useIntegrators } from '@/features/integrators/hooks';
import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/stores/sidebar';
import { useSessionStore } from '@/stores/session';
import { EffetaLogo } from '@/components/ui/icons/effeta-logo';
import { useHomeTarget } from '@/hooks/use-home-target';
import { bestMatchingNavPath } from '@/lib/nav-match';
import { filterNavGroups, type NavGroup } from './nav-groups';
import { SidebarToggle } from './SidebarToggle';

// Grupos, items y su orden viven en `nav-groups.ts`: los comparte con
// `useHomeTarget`, que necesita la primera sección en el orden del menú.

interface SidebarProps {
  className?: string;
  // Cuando se renderiza dentro del drawer mobile, queremos que el toggle
  // de colapsado no aparezca (no aplica en mobile) y que el item al hacer
  // click cierre el drawer.
  variant?: 'desktop' | 'mobile';
  onNavigate?: () => void;
}

export const Sidebar = ({ className, variant = 'desktop', onNavigate }: SidebarProps) => {
  const hasUser = useSessionStore((s) => s.user !== null);
  const permissions = useSessionStore((s) => s.user?.permissions);
  const collapsed = useSidebarStore((s) => s.collapsed);
  // Inicio solo si el dashboard tiene alguna tarjeta para el user.
  const home = useHomeTarget();

  const staticGroups = filterNavGroups(hasUser, permissions, home.hasDashboard);

  // Integradores viven en un group dinámico: la lista sale de
  // `GET /integrators` (filtrada por role del caller server-side). El
  // group aparece solo si hay al menos un integrador para este user.
  const integratorsQuery = useIntegrators();
  const dynamicGroups: NavGroup[] = [];
  if ((integratorsQuery.data?.length ?? 0) > 0) {
    dynamicGroups.push({
      label: 'Integraciones',
      items: integratorsQuery.data!.map((i) => ({
        to: `/integrators/${i.slug}`,
        label: i.name,
        icon: Puzzle,
      })),
    });
  }
  const groups = [...staticGroups, ...dynamicGroups];

  // Item activo resuelto acá y no por el `isActive` de cada NavLink: la
  // decisión necesita ver TODOS los items para quedarse con el más específico.
  const { pathname } = useLocation();
  const activePath = bestMatchingNavPath(
    pathname,
    groups.flatMap((g) => g.items.map((i) => i.to)),
  );

  // En mobile siempre se muestra el sidebar expandido (drawer ocupa toda la altura).
  const isCollapsed = variant === 'desktop' && collapsed;

  // Sombras de scroll: cada una aparece solo si hay contenido oculto de ese
  // lado. Se recalcula al scrollear y cuando cambia el alto (colapsar el
  // sidebar, sumar integradores, redimensionar la ventana).
  const navRef = useRef<HTMLElement | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  // Label flotante (colapsado): `true` mientras el nav scrollea, para tapar
  // labels con coordenadas viejas hasta el próximo hover/focus. Ver `onScroll`
  // del `<nav>` más abajo.
  const [labelsSuppressed, setLabelsSuppressed] = useState(false);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    function update(): void {
      const node = navRef.current;
      if (!node) return;
      // 1px de tolerancia: con zoom o pantallas HiDPI el scrollTop final
      // queda en 0.5 y la sombra parpadearía al llegar al borde.
      setCanScrollUp(node.scrollTop > 1);
      setCanScrollDown(node.scrollTop + node.clientHeight < node.scrollHeight - 1);
    }
    update();
    el.addEventListener('scroll', update, { passive: true });
    // `ResizeObserver` no existe en el entorno de tests; sin él la sombra
    // igual se calcula al montar y al scrollear.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [groups.length, isCollapsed]);

  return (
    <aside
      data-testid="sidebar"
      aria-label="Navegación principal"
      className={cn('border-border bg-surface text-text flex h-full flex-col border-r', className)}
    >
      {/* En escritorio el logo está en la barra de arriba (`Navbar`) y el
          menú empieza debajo de ella. El cajón del celular tapa la barra:
          ahí el logo se repite arriba del menú. Texto del logo en
          `currentColor` (navy en claro, blanco en oscuro); la tilde y el
          punto van en el rojo de la marca, fijo en el componente. */}
      {variant === 'mobile' ? (
        <div className="border-border flex h-16 items-center gap-2 border-b px-4">
          <EffetaLogo className="text-secondary h-7 w-auto dark:text-white" />
        </div>
      ) : null}

      {/* Lista de items agrupada. Envuelta en un container `relative` con
          fade top/bottom para senalizar visualmente que hay contenido
          fuera del viewport -- sin este overlay el sidebar tiene overflow
          silencioso y el user no se da cuenta que puede scrollear. La
          scrollbar custom (thin + tinted) da el segundo affordance. */}
      <div className="relative flex-1 overflow-hidden">
        <nav
          ref={navRef}
          data-tour="sidebar-nav"
          className="sidebar-scroll h-full overflow-y-auto p-2"
          onScroll={() => {
            // El label flotante es `position: fixed` (ver más abajo): no se
            // recoloca solo al scrollear. Si el mouse queda quieto sobre un
            // item que el scroll corrió, el navegador no vuelve a disparar
            // `mouseenter` y el label quedaría flotando en coordenadas viejas.
            // Más simple que perseguirlo: lo escondemos. Se reabre solo, ya
            // con coordenadas frescas, en el próximo hover/focus.
            if (isCollapsed) setLabelsSuppressed(true);
          }}
        >
          <ul className="flex flex-col gap-3">
            {groups.map((group, groupIdx) => (
              <li key={group.label}>
                {/* Header del grupo: uppercase pequeño en expanded; separator
                  fino en collapsed para que no quede todo pegado. */}
                {!isCollapsed ? (
                  <p
                    className="text-text-muted px-3 pt-1 pb-1 text-[10px] font-semibold tracking-wider uppercase"
                    aria-hidden="true"
                  >
                    {group.label}
                  </p>
                ) : groupIdx > 0 ? (
                  <div className="bg-border mx-2 mb-1 h-px" aria-hidden="true" />
                ) : null}
                <ul className="flex flex-col gap-1" role="group" aria-label={group.label}>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    // Nodo del label flotante de ESTE item. Se lee/escribe por
                    // ref, imperativo, no por state: solo mutamos su posición en
                    // hover/focus (con `getBoundingClientRect` del propio link),
                    // así evitamos un re-render por cada movimiento de mouse. El
                    // resto del ciclo de vida (abrir/cerrar, animar) lo sigue
                    // manejando CSS puro vía `group-hover`/`group-focus-visible`.
                    let labelNode: HTMLSpanElement | null = null;
                    // El diseño es UNA sola caja que "crece" desde el icono, no
                    // una pastilla aparte al lado: el overlay se ancla EXACTO
                    // sobre el item (`top`/`left`/`height` = su propio rect, sin
                    // offset) para que a simple vista sea la misma caja del rail.
                    // `--rail-w` es el ancho de esa caja en reposo (== ancho real
                    // del item, para que el arranque del `max-width` no salte) y
                    // solo así, en vez de con `left-full`/`top-1/2`, porque acá
                    // no aplican: son `position: fixed`, en coordenadas de
                    // viewport, no relativas al item.
                    //
                    // `position: fixed` en vez de `absolute`: medido en Chromium,
                    // `overflow-x: visible` en un eje se computa `auto` si el
                    // otro eje no es `visible`, y el nav interno usa
                    // `overflow-y-auto` — con `absolute` el label terminaba
                    // recortado en el borde del rail pese al `overflow-hidden`
                    // "arreglado" en el wrapper. `fixed` no se recorta con el
                    // overflow de ningún ancestro.
                    const positionLabel = (e: React.SyntheticEvent<HTMLAnchorElement>): void => {
                      if (!labelNode) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      labelNode.style.top = `${rect.top}px`;
                      labelNode.style.left = `${rect.left}px`;
                      labelNode.style.height = `${rect.height}px`;
                      labelNode.style.setProperty('--rail-w', `${rect.width}px`);
                      setLabelsSuppressed(false);
                    };
                    return (
                      <li key={item.to} className="group relative">
                        {/* `Link` y no `NavLink`: NavLink escribe su propio
                          `aria-current` DESPUÉS de las props recibidas, así que
                          pisaba el nuestro y seguía marcando dos items. El
                          estado activo lo decide `bestMatchingNavPath`, que es
                          la única fuente de verdad acá. */}
                        <Link
                          to={item.to}
                          onClick={onNavigate}
                          onMouseEnter={isCollapsed ? positionLabel : undefined}
                          onFocus={isCollapsed ? positionLabel : undefined}
                          // `title` era el tooltip nativo del navegador: delay fijo
                          // ~1s, sin estilo. Reemplazado por el label flotante de
                          // abajo. El nombre accesible del link no puede depender
                          // de ese label (solo visible en hover/focus), así que
                          // colapsado lo exponemos por `aria-label`; expandido el
                          // texto visible ya cumple ese rol, como antes.
                          aria-label={isCollapsed ? item.label : undefined}
                          data-tour={item.dataTour}
                          // `isActive` del router NO se usa: marca por prefijo y
                          // encendería Productos estando en `/products/query`.
                          // Ver `bestMatchingNavPath`.
                          aria-current={item.to === activePath ? 'page' : undefined}
                          className={cn(
                            // `group` también acá, no solo en el <li>: la caja
                            // flotante es hija del link, y para abrirla con el
                            // foco de TECLADO hace falta que el elemento del
                            // grupo sea el que recibe foco (un <li> no lo
                            // recibe). Ver el comentario de `group-focus-visible`
                            // abajo.
                            'group focus-visible:ring-ring flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-hidden',
                            // Dónde se está va en una pastilla y en negrita,
                            // como en Effetá Apps: no depende solo del color.
                            item.to === activePath
                              ? 'bg-nav-active text-nav-active-fg font-semibold'
                              : 'text-text-muted hover:bg-surface-2 hover:text-text font-medium',
                            isCollapsed && 'justify-center px-2',
                          )}
                        >
                          <>
                            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {!isCollapsed ? <span>{item.label}</span> : null}
                            {isCollapsed ? (
                              // El icono se repite acá: es la MISMA caja que la
                              // de abajo, solo que flotando y más ancha, no un
                              // tooltip aparte — por eso trae su propio icono
                              // fijo a la izquierda en vez de mostrar el label
                              // solo. `overflow-hidden` + `items-center` (sin
                              // `justify-center`) clava el icono en el borde
                              // izquierdo: al crecer el `max-width` la caja se
                              // extiende a la derecha y el icono nunca se
                              // mueve. Mismo fondo/estado que el item real
                              // (`bg-surface-2`, o la pastilla si está activo)
                              // para que hover sobre el activo no lo apague.
                              <span
                                ref={(el) => {
                                  labelNode = el;
                                }}
                                aria-hidden="true"
                                className={cn(
                                  'pointer-events-none fixed z-50 flex items-center gap-3 overflow-hidden rounded-md px-2 py-2 text-sm',
                                  'max-w-(--rail-w,2rem) opacity-0 transition-[max-width,opacity] duration-120 ease-out motion-reduce:transition-none',
                                  'group-hover:max-w-[16rem] group-hover:opacity-100',
                                  // `focus-visible` y NO `focus-within`: al
                                  // hacer click, el link QUEDA con el foco
                                  // después de navegar, así que con
                                  // `focus-within` la caja seguía abierta en la
                                  // pantalla nueva hasta clickear en cualquier
                                  // otro lado. `focus-visible` solo se activa
                                  // con foco de teclado, que es para quien la
                                  // caja hace falta.
                                  'group-focus-visible:max-w-[16rem] group-focus-visible:opacity-100',
                                  item.to === activePath
                                    ? 'bg-nav-active text-nav-active-fg font-semibold'
                                    : 'bg-surface-2 text-text font-medium',
                                  labelsSuppressed && 'invisible',
                                )}
                              >
                                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span className="whitespace-nowrap">{item.label}</span>
                              </span>
                            ) : null}
                          </>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
        {/* Fade overlays. `from-surface` iguala el bg del sidebar y muere
            en transparent para que se lea como "hay mas contenido detras".
            `pointer-events-none` para no bloquear clicks en los items del
            borde.
            Cada uno aparece SOLO si hay contenido de ese lado: arriba del
            todo la sombra superior no señala nada y se lee como un borde
            sucio. */}
        {canScrollUp && (
          <div
            data-testid="sidebar-fade-top"
            className="from-surface pointer-events-none absolute inset-x-0 top-0 h-6 bg-linear-to-b to-transparent"
          />
        )}
        {canScrollDown && (
          <div
            data-testid="sidebar-fade-bottom"
            className="from-surface pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-linear-to-t to-transparent"
          />
        )}
      </div>

      {/* Botón colapsar (solo desktop) */}
      {variant === 'desktop' ? (
        <div
          className={cn(
            'border-border flex border-t p-2',
            isCollapsed ? 'justify-center' : 'justify-end',
          )}
        >
          <SidebarToggle />
        </div>
      ) : null}
    </aside>
  );
};
